import { Link } from "@/lib/nav";

import CustomerFooter from "@/components/customer-footer";
import CustomerScreenShell from "@/components/customer-screen-shell";
import SharedNotice from "@buildpy/ui/notice";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { formatDate } from "@buildpy/core/lib/format";
import CustomerStatementView from "@/modules/change-order/ui/customer-statement-view";
import { STATEMENT_UNSIGNED_BODY, STATEMENT_UNSIGNED_TITLE } from "@buildpy/core/modules/contract/customer-link-text";
import UnusableLink from "@/modules/contract/ui/unusable-link";
import { contractSharePath } from "@buildpy/core/modules/contract/view";
import { verifyContractShare } from "@/api/actions/contract-share";
import PrintButton from "@/modules/print/print-button";
import ShareGate from "@/modules/share/ui/share-gate";
import CustomerHeadOrg from "@/modules/share/ui/customer-head-org";
import type { CustomerStatementPageView } from "@buildpy/core/views/g2b/customer-statement";

interface Props {
  data: CustomerStatementPageView;
  token: string;
}

const Notice = ({ text }: { text: string }) => (
  <CustomerScreenShell>
    <SectionCard as="div" className="mx-auto w-full max-w-sm p-5 text-center text-sm" padding="none" role="alert">
      {text}
    </SectionCard>
  </CustomerScreenShell>
);

// 고객 현재 내역서 — 「내 현장」과 같은 입장(체결된 살아 있는 링크 + 입장 쿠키)·분당 한도를 거쳐 직원 화면과 같은 계산 한 벌을 읽기 전용으로 보인다.
const Page = ({ data, token }: Props) => {
  if (data.kind === "unsigned") {
    return (
      <CustomerScreenShell>
        <SharedNotice
          actions={
            <Button asChild size="sm" variant="outline">
              <Link href={contractSharePath(token)}>계약서 보기</Link>
            </Button>
          }
          body={STATEMENT_UNSIGNED_BODY}
          title={STATEMENT_UNSIGNED_TITLE}
        />
      </CustomerScreenShell>
    );
  }
  if (data.kind === "unusable") return <UnusableLink />;
  if (data.kind === "notice") return <Notice text={data.text} />;
  const { orgName } = data;
  if (data.kind === "gate") {
    return (
      <CustomerScreenShell>
        <ShareGate docName="현재 내역서" orgName={orgName} token={token} verify={verifyContractShare} />
      </CustomerScreenShell>
    );
  }

  const { company, contract } = data;
  const contractHref = contractSharePath(token);

  return (
    <CustomerScreenShell className="p-0 pb-10">
      <header className="print-hide sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-4 pb-2.5 pt-safe-2.5">
        <CustomerHeadOrg name={orgName} tel={company.tel} />
        <div className="flex shrink-0 items-center gap-x-3">
          <Link className="tap-target relative text-sm text-muted-foreground underline-offset-2 hover:underline" href={contractHref}>
            계약서 보기
          </Link>
          <PrintButton label="인쇄" variant="outline" />
        </div>
      </header>

      <CustomerStatementView
        asOf={formatDate(new Date().toISOString())}
        awaiting={data.awaiting}
        contractHref={contractHref}
        contractNo={contract.contract_no}
        contractTitle={contract.title}
        statement={data.statement}
        token={token}
      />
      <CustomerFooter className="px-4" show={data.footer} />
    </CustomerScreenShell>
  );
};

export default Page;
